import { useState, useRef, useEffect } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Prediction } from "@/hooks/useGooglePlaces";

interface PlaceAutocompleteProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (prediction: Prediction) => void;
  predictions: Prediction[];
  loading: boolean;
  onSearch: (input: string) => void;
  iconColor?: string;
}

const PlaceAutocomplete = ({
  placeholder,
  value,
  onChange,
  onSelect,
  predictions,
  loading,
  onSearch,
  iconColor = "text-primary",
}: PlaceAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (predictions.length > 0) {
      setOpen(true);
    }
  }, [predictions]);

  return (
    <div ref={wrapperRef} className="relative">
      <MapPin className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 z-10", iconColor)} />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-white/40" />
      )}
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        className="flex h-12 w-full rounded-md border border-white/10 bg-white/5 px-3 pl-11 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
      />
      {open && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => {
                onSelect(p);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-3 text-sm text-popover-foreground hover:bg-accent transition-colors flex items-start gap-2"
            >
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span className="flex-1">{p.description}</span>
              {p.source === "db" && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full shrink-0">
                  Local
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaceAutocomplete;
